import { useState, useEffect, useCallback, useRef, MutableRefObject } from "react";
import { DragSide, calculateDragSide } from "./geometry";
import * as typ from "./types";
import { WithRequired } from "./utils";

export enum ElementRole {
  matrix = 1,
  row,
  item,
  virtual,
  self,
}

interface ElementInfo {
  rect(): DOMRect;
  id: string;
  numCols: number;
  pos?: typ.ItemPos;
  role?: ElementRole;
}

interface DefinedElementInfo extends ElementInfo{
  pos: typ.ItemPos;
  role: ElementRole;
}

export interface DraggedElementInfo extends DefinedElementInfo {
  startRect?: DOMRect;
}

export interface DragInfo {
  id: string;
  x: number;
  y: number;
  numCols: number;
  col: number;
}

export interface DragResult {
  dragged: DragInfo;
  over: DefinedElementInfo;
  side?: DragSide;
}

const VIRTUAL_ID = "__virtual__";
export const UNDEFINED_ID = "__undefined__";

export const useDrag = (matrix: typ.Matrix, columnWidth: number) => {
  const [result, setResult] = useState<DragResult>();
  
  const drag = useCallback((info: DragInfo) => {
    let rowInfo: [typ.Row, DOMRect, number] | undefined;

    // находим строку, над которой тащим
    for (let i = 0; i < matrix.rows.length; i++) {
      const r_ = matrix.rows[i] as WithRequired<typ.Row, "rect">;
      if (typeof r_.rect !== "function") {
        continue;
      }
      const rowRect_ = r_.rect();
      if (info.y > rowRect_.top && info.y <= rowRect_.bottom) {
        rowInfo = [r_, rowRect_, i];
        break;
      }
    }

    if (!rowInfo) return;
    const [r, rowRect, row] = rowInfo;

    const dragCol = Math.floor((info.x - rowRect.left) / columnWidth);
    const colStart = dragCol - info.col;

    const el = r.at(dragCol) as WithRequired<typ.Item, "rect">;;
    if (el && el.id !== info.id) {
      const rect = el.rect();
      const xi = info.x - rect.left;
      const yi = info.y - rect.top;
      //console.log("SIDE CALC", info.x, info.y, xi, yi, rect.left, rect.top, rect.width, rect.height);
      const side = calculateDragSide(xi, yi, rect.width, rect.height);
      const result: DragResult = {
        over: {
          ...el,
          pos: {row, col: el.pos.col},
          role: ElementRole.item,
        },
        dragged: info,
        side,
      };
      //console.log("A", result);
      setResult(result);
      return;
    }

    const elWidth = info.numCols*columnWidth;
    const elLeft = rowRect.left + colStart*columnWidth;
    const result: DragResult = {
      over: {
        rect: () => ({
          ...rowRect,
          left: elLeft,
          right: elLeft + elWidth,
          width: elWidth,
          x: elLeft,
        }),
        id: VIRTUAL_ID,
        pos: {row, col: colStart},
        role: ElementRole.virtual,
        numCols: info.numCols,
      },
      dragged: info,
    };
    //console.log("B", result);
    setResult(result);
  }, [matrix.rows, columnWidth]);

  return {
    result,
    drag,
    stop() { setResult(undefined); }
  }
}

export type ItemMouseEvent<El extends HTMLElement> = React.MouseEvent<El>
export type DropHandler = (res: DragResult) => void;

const useInternalDND = <El extends HTMLElement,>(
  matrix: typ.Matrix, 
  columnWidth: number, 
  onDrop: DropHandler, 
) => {
  const [dragged, setDragged] = useState<DraggedElementInfo>();
  const [dragPos, setDragPos] = useState<[number, number]>();
  const [dragStartPos, setDragStartPos] = useState<[number, number]>();
  
  const moveCB = useRef<((e: globalThis.MouseEvent) => void) | null>(null);
  const upCB = useRef<((e: globalThis.MouseEvent) => void) | null>(null);

  const dragger = useDrag(matrix, columnWidth);

  const onMouseUp = useCallback(() => { 
    if (moveCB.current) {
      window.removeEventListener("mousemove", moveCB.current);
      moveCB.current = null;
    }

    if (upCB.current) {
      window.removeEventListener("mouseup", upCB.current);
      upCB.current = null;
    }

    if (dragger.result) onDrop(dragger.result);
    
    dragger.stop();
    setDragged(undefined);
    setDragPos(undefined);
  }, [onDrop]);

  useEffect(() => {
    if (upCB.current) {
      window.removeEventListener("mouseup", upCB.current);
      window.addEventListener("mouseup", onMouseUp);
      upCB.current = onMouseUp;
      //return () => {
      //  window.removeEventListener("mouseup", onMouseUp);
      //  upCB.current = null;
      //}
    }
  }, [onMouseUp]);

  const doDrag = useCallback((ev: ItemMouseEvent<El> | globalThis.MouseEvent, item?: typ.Item) => {
    if (!dragged) return;
      dragger.drag({
        id: dragged.id,
        x: ev.clientX,
        y: ev.clientY,
        numCols: dragged.numCols,
        col: Math.floor((ev.clientX - dragged.rect().left)/columnWidth),
      });
  }, [dragged, dragger]);

  const onMouseMove = useCallback((ev: globalThis.MouseEvent) => {
    if (!dragStartPos) return;
    const [startX, startY] = dragStartPos;
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    setDragPos([dx, dy]);
    doDrag(ev);
  }, [doDrag, dragStartPos]);

  useEffect(() => {
    if (moveCB.current) {
      window.removeEventListener("mousemove", moveCB.current);
      window.addEventListener("mousemove", onMouseMove);
      moveCB.current = onMouseMove;
      //return () => {
      //  window.removeEventListener("mousemove", onMouseMove);
      //  moveCB.current = null;
      //}
    }
  }, [onMouseMove]);

  const dragStart = (ev: ItemMouseEvent<El>, el: MutableRefObject<El>, item: typ.Item) => {
    setDragged({
      rect: () => {
        const rect = el.current?.getBoundingClientRect();
        return rect;
      },
      id: item.id,
      pos: item.pos,
      role: ElementRole.item,
      numCols: item.numCols,
      startRect: (ev.target as El).getBoundingClientRect(),
    });
    setDragStartPos([ev.clientX, ev.clientY]);
    upCB.current = onMouseUp;
    moveCB.current = onMouseMove;
    window.addEventListener("mousemove", moveCB.current);
    window.addEventListener("mouseup", upCB.current);
  };

  return {
    dragged: dragger.result,
    dragPos,
    dragStart,
  };
}

export default useInternalDND;